import {
  TextInputField,
  DatePickerField,
  DropdownField,
  TagsDropdownField,
  ImageInputField,
  TextAreaField,
} from "../../../../../../components/inputs";
import { StyledHorizontalSpace } from "./style";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../../../store/config";
import { useEffect, useState } from "react";
import {
  createTrainerStart,
  updateTrainerStart,
} from "../../../../../../store/features/trainer/trainer-slice";
import { useParams } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { Form, notification, Spin } from "antd";
import { useFilterTags, useInterestsTags } from "../../../../useSelectOptions";
import dayjs from "dayjs";
import {
  AbsoluteButton75,
  RelativeFlex,
  StyledColumnWrapper,
  StyledForm,
  StyledLayout,
} from "../../../../../../components/common/styles";
import { IUploadFileParams } from "../../../../../../interfaces/files/files";
import { IMAGE_BASE_URL } from "../../../../../../common/config";
import {
  ICreateTrainerPayload,
  IUpdateTrainerPayload,
} from "../../../../../../interfaces/store/trainer-interfaces";
import { blobToBase64 } from "../../../../../../utils/utils";
import { ForgotPasswordModal } from "../../../../../../components/forgot-password/ForgotPassword";

export const TrainerPersonalDetails = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const { id: trainerId } = useParams<{ id: string }>();
  const [api, contextHolder] = notification.useNotification();
  const { isEdit } = location.state || {};

  const isNew: boolean = location.pathname === "/trainers/add";

  const [isEditable, setIsEditable] = useState<boolean>(false);
  const [imageData, setImageData] = useState<IUploadFileParams | null>(null);

  const { trainer, loading, error, requestState, operation } = useAppSelector(
    (state) => state.trainer
  );

  useEffect(() => {
    if (isNew || isEdit) {
      setIsEditable(true);
    }
  }, [dispatch, isNew, trainerId]);

  useEffect(() => {
    if (
      requestState === "finish" &&
      (operation === "update" || operation === "create")
    ) {
      setIsEditable(false);
    }
  }, [requestState, operation]);

  useEffect(() => {
    if (trainer) {
      form.setFieldsValue({
        firstName: trainer.firstName,
        lastName: trainer.lastName,
        email: trainer.email,
        phone: trainer.phone,
        gender: trainer.gender,
        dob: trainer.dob ? dayjs(trainer.dob) : null,
        specializations: trainer.fitnessProfile?.trainerData?.specializations,
        subSpecializations:
          trainer.fitnessProfile?.trainerData?.subSpecializations,
        description: trainer.fitnessProfile?.trainerData?.description,
      });
    }
  }, [trainer]);

  useEffect(() => {
    if (error) {
      openNotification(error.status === 409 ? error.message : undefined);
    }
  }, [error]);

  const openNotification = (message?: string) => {
    api["error"]({
      message: message
        ? t(message)
        : t("Something went wrong, please try again"),
      placement: "bottomLeft",
    });
  };

  const upsertTrainer = async (values: any) => {
    if (!isEditable) {
      setIsEditable(true);
      return;
    }

    const accountData = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      phone: values.phone,
      dob: values.dob,
      gender: values.gender,
    };

    const trainerData = {
      specializations: values.specializations,
      subSpecializations: values.subSpecializations,
      description: values.description,
    };

    const imageDataPayload = imageData
      ? {
          ...imageData,
          binaryFile: await blobToBase64(imageData.binaryFile as Blob),
          fileType: "image",
        }
      : null;

    const upsertTrainerBody = {
      trainer: isNew
        ? { ...accountData, trainerData }
        : { account: accountData, fitnessProfile: { trainerData } },
      ...(trainerId && { userId: trainerId }),
      ...(imageDataPayload && { imageData: imageDataPayload }),
    };

    dispatch(
      isNew
        ? createTrainerStart(upsertTrainerBody as ICreateTrainerPayload)
        : updateTrainerStart(upsertTrainerBody as IUpdateTrainerPayload)
    );
  };

  return (
    <>
      {contextHolder}
      <RelativeFlex>
        <AbsoluteButton75 onClick={() => form.submit()}>
          {isEditable ? t("Save") : t("Edit")}
        </AbsoluteButton75>
      </RelativeFlex>

      <Spin spinning={loading}>
        <StyledForm
          form={form}
          onFinish={upsertTrainer}
          validateTrigger="onSubmit"
        >
          <StyledLayout>
            <StyledColumnWrapper>
              <StyledHorizontalSpace>
                <TextInputField
                  label={t("First Name")}
                  isEditable={isEditable}
                  name="firstName"
                  rules={[{ required: true, message: t("Required") }]}
                />
                <TextInputField
                  label={t("Last Name")}
                  isEditable={isEditable}
                  name="lastName"
                  rules={[{ required: true, message: t("Required") }]}
                />
              </StyledHorizontalSpace>
              <StyledHorizontalSpace>
                <TextInputField
                  label={t("Email Address")}
                  isEditable={isEditable}
                  name="email"
                  rules={[
                    { required: true, message: t("Required") },
                    {
                      type: "email",
                      message: t("Invalid email address"),
                    },
                  ]}
                />
                <TextInputField
                  label={t("Phone Number")}
                  isEditable={isEditable}
                  name="phone"
                  rules={[
                    { required: true, message: t("Required") },
                    {
                      pattern: /^[0-9]*$/,
                      message: t("Invalid phone number"),
                    },
                  ]}
                />
              </StyledHorizontalSpace>
              <StyledHorizontalSpace>
                <DropdownField
                  label={t("Gender")}
                  options={[
                    { label: t("Male"), value: "MALE" },
                    { label: t("Female"), value: "FEMALE" },
                  ]}
                  // value={trainer?.gender}
                  isEditable={isEditable}
                  name="gender"
                  rules={[{ required: true, message: t("Required") }]}
                />
                <DatePickerField
                  label={t("Date of Birth")}
                  isEditable={isEditable}
                  name="dob"
                  rules={[{ required: true, message: t("Required") }]}
                  maxDate={dayjs()}
                />
              </StyledHorizontalSpace>
              <TagsDropdownField
                mode="multiple"
                label={t("Specializations")}
                options={useInterestsTags()}
                name="specializations"
                isEditable={isEditable}
              />
              <TagsDropdownField
                mode="multiple"
                label={t("Sub-Specializations")}
                options={useFilterTags()}
                name="subSpecializations"
                isEditable={isEditable}
              />
              <TextAreaField
                label={t("Description")}
                isEditable={isEditable}
                name="description"
              />
            </StyledColumnWrapper>
            <StyledColumnWrapper>
              <ImageInputField
                label={t("Profile Picture")}
                value={
                  trainer?.avatar
                    ? IMAGE_BASE_URL + "/" + trainer?.avatar
                    : undefined
                }
                isEditable={isEditable}
                onChange={setImageData}
                aspect={130 / 144}
              />

              <ForgotPasswordModal
                openNotification={openNotification}
                email={trainer?.email}
              />
            </StyledColumnWrapper>
          </StyledLayout>
        </StyledForm>
      </Spin>
    </>
  );
};
